// ClientCardUpdatePage.jsx
// Public page reached via an emailed single-use link (/update-card?token=...).
// The client enters a card; it is sent straight to Authorize.Net by the server
// and added to their profile. No card data is stored in the portal database.
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const wrap = {
  minHeight: '100vh', background: 'var(--surface-muted)', padding: '24px',
  fontFamily: 'Arial, sans-serif', display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
};
const card = {
  background: '#fff', borderRadius: 12, boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
  width: '100%', maxWidth: 460, overflow: 'hidden', marginTop: 32,
};
const header = { background: 'var(--primary-color)', color: '#ffffff', padding: '22px 24px' };
const body = { padding: 24 };
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)', marginBottom: 4 };
const input = {
  width: '100%', padding: '9px 10px', borderRadius: 6,
  border: '1px solid var(--border-color)', fontSize: 14, boxSizing: 'border-box',
};
const btn = {
  background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6,
  padding: '11px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 15, width: '100%',
};

function getToken() {
  return new URLSearchParams(window.location.search).get('token') || '';
}

const REASON_MSG = {
  invalid: 'This link is not valid. Please contact the office for a new one.',
  expired: 'This link has expired. Please contact the office for a new one.',
  used: 'This link has already been used. If you need to update your card again, please ask the office for a new link.',
  error: 'Something went wrong checking this link. Please try again later.',
};

export default function ClientCardUpdatePage() {
  const [token] = useState(getToken);
  const [status, setStatus] = useState('loading'); // loading | ready | invalid | done
  const [reason, setReason] = useState('');
  const [clientName, setClientName] = useState('');

  const [form, setForm] = useState({
    firstName: '', lastName: '', companyName: '',
    cardNumber: '', expirationDate: '', cardCode: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setStatus('invalid'); setReason('invalid'); return; }
    axios.get(`/api/forms/card-update/validate?token=${encodeURIComponent(token)}`)
      .then(res => {
        if (res.data?.success) {
          setClientName(res.data.clientName || '');
          setStatus('ready');
        } else {
          setReason(res.data?.reason || 'invalid');
          setStatus('invalid');
        }
      })
      .catch(() => { setReason('error'); setStatus('invalid'); });
  }, [token]);

  const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.cardNumber || !form.expirationDate || !form.cardCode) {
      setError('Card number, expiration, and CVV are required.');
      return;
    }
    setSubmitting(true); setError('');
    try {
      const res = await axios.post('/api/forms/card-update/submit', { token, card: form });
      if (res.data?.success) {
        setStatus('done');
      } else {
        setError(res.data?.message || 'Could not save the card. Please try again.');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save the card. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={header}>
          <h2 style={{ margin: 0, fontSize: 23, fontWeight: 800, color: '#ffffff', letterSpacing: 0.2, textShadow: '0 1px 2px rgba(0,0,0,0.35)' }}>Update Your Card on File</h2>
          <div style={{ fontSize: 13, color: '#ffffff', opacity: 0.95, marginTop: 5 }}>Avenues Unlimited Counseling Center</div>
        </div>
        <div style={body}>
          {status === 'loading' && <p style={{ color: '#64748b' }}>Checking your link…</p>}

          {status === 'invalid' && (
            <p style={{ color: '#b91c1c', fontSize: 15 }}>{REASON_MSG[reason] || REASON_MSG.invalid}</p>
          )}

          {status === 'done' && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: 40 }}>✅</div>
              <h3 style={{ margin: '8px 0' }}>Card saved</h3>
              <p style={{ color: 'var(--text-strong)' }}>Thank you{clientName ? `, ${clientName}` : ''}. Your card has been securely saved. You can close this page.</p>
            </div>
          )}

          {status === 'ready' && (
            <>
              <p style={{ marginTop: 0, color: 'var(--text-strong)', fontSize: 14 }}>
                {clientName ? `${clientName}, please` : 'Please'} enter the card you'd like us to keep on file. Your details are sent securely to our payment processor and are never stored on this site.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={label}>First Name</label>
                  <input style={input} value={form.firstName} onChange={setField('firstName')} />
                </div>
                <div>
                  <label style={label}>Last Name</label>
                  <input style={input} value={form.lastName} onChange={setField('lastName')} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={label}>Company / Note (optional)</label>
                  <input style={input} value={form.companyName} onChange={setField('companyName')} placeholder='e.g. HSA card' />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={label}>Card Number</label>
                  <input style={input} value={form.cardNumber} onChange={setField('cardNumber')} inputMode="numeric" autoComplete="cc-number" />
                </div>
                <div>
                  <label style={label}>Expiration (YYYY-MM)</label>
                  <input style={input} value={form.expirationDate} onChange={setField('expirationDate')} placeholder="2030-12" autoComplete="cc-exp" />
                </div>
                <div>
                  <label style={label}>CVV</label>
                  <input style={input} value={form.cardCode} onChange={setField('cardCode')} inputMode="numeric" autoComplete="cc-csc" />
                </div>
              </div>

              {error && (
                <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{error}</div>
              )}

              <button style={{ ...btn, opacity: submitting ? 0.7 : 1 }} onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Saving…' : 'Save Card'}
              </button>
              <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 12 }}>
                🔒 Secured by Authorize.Net. This site never stores your full card number.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
